import React from 'react';

interface MarkdownRendererProps {
  content: string;
  className?: string;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, className = '' }) => {
  if (!content) return null;

  // Simple markdown parser that handles:
  // - Line breaks (\n\n for paragraphs, \n for line breaks)
  // - Bold (**text**)
  // - Italic (*text*)
  // - Code (`code`)
  // - Links [text](url)
  // - Headings (### H3, ## H2, # H1)
  // - Unordered lists (- item, * item, + item)
  // - Ordered lists (1. item, 2. item, etc.)
  
  const renderContent = (): React.ReactNode => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;
    let keyCounter = 0;
    
    while (i < lines.length) {
      const line = lines[i].trim();
      
      // Skip empty lines
      if (line === '') {
        i++;
        continue;
      }
      
      // Check for headings: ### H3, ## H2, # H1
      const h3Match = line.match(/^###\s+(.+)$/);
      if (h3Match) {
        elements.push(
          <h3 key={`h3-${keyCounter++}`} className="font-display text-base font-semibold mb-2 mt-4 first:mt-0" style={{ color: 'var(--gp-ink)' }}>
            {processInlineMarkdown(h3Match[1])}
          </h3>
        );
        i++;
        continue;
      }
      
      const h2Match = line.match(/^##\s+(.+)$/);
      if (h2Match) {
        elements.push(
          <h2 key={`h2-${keyCounter++}`} className="font-display text-lg font-semibold mb-2 mt-5 first:mt-0" style={{ color: 'var(--gp-ink)' }}>
            {processInlineMarkdown(h2Match[1])}
          </h2>
        );
        i++;
        continue;
      }
      
      const h1Match = line.match(/^#\s+(.+)$/);
      if (h1Match) {
        elements.push(
          <h1 key={`h1-${keyCounter++}`} className="font-display text-xl font-semibold mb-3 mt-2 first:mt-0" style={{ color: 'var(--gp-ink)' }}>
            {processInlineMarkdown(h1Match[1])}
          </h1>
        );
        i++;
        continue;
      }

      // Fenced code blocks
      if (line.startsWith('```')) {
        const codeLines: string[] = [];
        i++;
        while (i < lines.length && !lines[i].trim().startsWith('```')) {
          codeLines.push(lines[i]);
          i++;
        }
        if (i < lines.length) i++;
        elements.push(
          <pre
            key={`pre-${keyCounter++}`}
            className="text-xs sm:text-sm font-mono p-3 rounded-md overflow-x-auto mb-3 whitespace-pre-wrap"
            style={{ background: 'var(--gp-surface-muted)', color: 'var(--gp-ink)', border: '1px solid var(--gp-border)' }}
          >
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        continue;
      }

      // Tables (pipe rows)
      if (line.includes('|') && line.trim().startsWith('|')) {
        const tableRows: string[][] = [];
        while (i < lines.length) {
          const rowLine = lines[i].trim();
          if (!rowLine.includes('|') || !rowLine.startsWith('|')) break;
          if (/^\|[\s-:|]+\|$/.test(rowLine)) {
            i++;
            continue;
          }
          const cells = rowLine
            .split('|')
            .slice(1, -1)
            .map((c) => c.trim());
          tableRows.push(cells);
          i++;
        }
        if (tableRows.length > 0) {
          const [header, ...body] = tableRows;
          elements.push(
            <div key={`table-${keyCounter++}`} className="gp-table-wrap mb-3 overflow-x-auto">
              <table>
                <thead>
                  <tr>
                    {header.map((cell, ci) => (
                      <th key={ci}>{processInlineMarkdown(cell)}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {body.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => (
                        <td key={ci}>{processInlineMarkdown(cell)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        continue;
      }
      
      // Check for unordered list: - item, * item, or + item
      const unorderedMatch = line.match(/^[-*+]\s+(.+)$/);
      if (unorderedMatch) {
        const listItems: React.ReactNode[] = [];
        while (i < lines.length) {
          const currentLine = lines[i].trim();
          const itemMatch = currentLine.match(/^[-*+]\s+(.+)$/);
          if (itemMatch) {
            listItems.push(
              <li key={`ul-item-${keyCounter++}`} className="ml-4 mb-1">
                {processInlineMarkdown(itemMatch[1])}
              </li>
            );
            i++;
          } else {
            break;
          }
        }
        elements.push(
          <ul key={`ul-${keyCounter++}`} className="list-disc list-inside mb-2 space-y-1 ml-4">
            {listItems}
          </ul>
        );
        continue;
      }
      
      // Check for ordered list: 1. item, 2. item, etc.
      const orderedMatch = line.match(/^\d+\.\s+(.+)$/);
      if (orderedMatch) {
        const listItems: React.ReactNode[] = [];
        while (i < lines.length) {
          const currentLine = lines[i].trim();
          const itemMatch = currentLine.match(/^\d+\.\s+(.+)$/);
          if (itemMatch) {
            listItems.push(
              <li key={`ol-item-${keyCounter++}`} className="ml-4 mb-1">
                {processInlineMarkdown(itemMatch[1])}
              </li>
            );
            i++;
          } else {
            break;
          }
        }
        elements.push(
          <ol key={`ol-${keyCounter++}`} className="list-decimal list-inside mb-2 space-y-1 ml-4">
            {listItems}
          </ol>
        );
        continue;
      }
      
      // Regular paragraph - collect consecutive non-list, non-heading lines
      const paragraphLines: string[] = [];
      while (i < lines.length) {
        const currentLine = lines[i].trim();
        if (currentLine === '') {
          break;
        }
        // Stop if we hit a list, heading, code fence, or table
        if (currentLine.match(/^[-*+]\s+/) || 
            currentLine.match(/^\d+\.\s+/) ||
            currentLine.match(/^#{1,3}\s+/) ||
            currentLine.startsWith('```') ||
            (currentLine.includes('|') && currentLine.startsWith('|'))) {
          break;
        }
        paragraphLines.push(currentLine);
        i++;
      }
      
      if (paragraphLines.length > 0) {
        elements.push(
          <p key={`p-${keyCounter++}`} className="mb-2 last:mb-0">
            {paragraphLines.map((line, lineIndex) => (
              <React.Fragment key={`line-${lineIndex}`}>
                {processInlineMarkdown(line)}
                {lineIndex < paragraphLines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      }
    }
    
    return elements;
  };
  
  const processInlineMarkdown = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let keyCounter = 0;
    let remainingText = text;
    
    // Process in order: code, bold, italic, links
    while (remainingText.length > 0) {
      let found = false;
      
      // Inline code: `code` (highest priority)
      const codeMatch = remainingText.match(/`([^`]+)`/);
      if (codeMatch && codeMatch.index === 0) {
        parts.push(
          <code key={`code-${keyCounter++}`} className="px-1.5 py-0.5 rounded text-sm font-mono" style={{ background: 'var(--gp-surface-muted)', color: 'var(--gp-ink)' }}>
            {codeMatch[1]}
          </code>
        );
        remainingText = remainingText.substring(codeMatch[0].length);
        found = true;
        continue;
      }
      
      // Bold: **text**
      const boldMatch = remainingText.match(/\*\*([^*]+)\*\*/);
      if (boldMatch && boldMatch.index === 0) {
        parts.push(
          <strong key={`bold-${keyCounter++}`} className="font-semibold">{boldMatch[1]}</strong>
        );
        remainingText = remainingText.substring(boldMatch[0].length);
        found = true;
        continue;
      }
      
      // Italic: *text* (but not **text**)
      const italicMatch = remainingText.match(/^\*([^*]+)\*/);
      if (italicMatch && italicMatch.index === 0) {
        parts.push(
          <em key={`italic-${keyCounter++}`} className="italic">{italicMatch[1]}</em>
        );
        remainingText = remainingText.substring(italicMatch[0].length);
        found = true;
        continue;
      }
      
      // Links: [text](url)
      const linkMatch = remainingText.match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch && linkMatch.index === 0) {
        parts.push(
          <a
            key={`link-${keyCounter++}`}
            href={linkMatch[2]}
            target="_blank"
            rel="noopener noreferrer"
            className="gp-link hover:underline"
          >
            {linkMatch[1]}
          </a>
        );
        remainingText = remainingText.substring(linkMatch[0].length);
        found = true;
        continue;
      }
      
      // No match found, add text up to next potential match
      if (!found) {
        // Find the earliest match position
        const codePos = remainingText.indexOf('`');
        const boldPos = remainingText.indexOf('**');
        const italicPos = remainingText.indexOf('*');
        const linkPos = remainingText.indexOf('[');
        
        const positions = [codePos, boldPos, italicPos, linkPos].filter(p => p !== -1);
        const nextPos = positions.length > 0 ? Math.min(...positions) : remainingText.length;
        
        if (nextPos > 0) {
          parts.push(remainingText.substring(0, nextPos));
          remainingText = remainingText.substring(nextPos);
        } else {
          // No more matches, add remaining text
          parts.push(remainingText);
          break;
        }
      }
    }
    
    return parts.length > 0 ? parts : [text];
  };
  
  return (
    <div className={`markdown-content ${className}`}>
      {renderContent()}
    </div>
  );
};

export default MarkdownRenderer;

